/**
 * Shared rate-calculation logic used by:
 *   - ClientInvoicesPage  → preview client charge for completed shifts
 *   - RosterCalendarPage  → preview both staff pay & client charge while admins
 *                            create/edit a shift
 *   - payrollService      → preview staff pay on the Completed Work table
 *
 * Mirrors the backend's `BuildLineItems` (Billing) and `BuildShiftSegments`
 * (StaffPayroll) so the UI numbers match what payroll/invoicing will actually
 * generate.
 */
import type { BillingRate, FlatRate } from '../services/newBillingService';

export interface RateSlice {
  name: string;
  /** For Hourly: hours worked. For Travel: kilometres. For Sleepover: 1. */
  hours: number;
  /** Effective $/unit (after applying segment percentage to base). */
  rate: number;
  amount: number;
  kind?: 'Hourly' | 'Sleepover' | 'Travel';
}

export interface RateBreakdown {
  total: number;
  segments: RateSlice[];
  /** Hours that fell outside any active segment for that day type. */
  uncoveredHours: number;
  /** Reason the estimate is partial / unavailable (e.g. missing FlatRate). */
  warning?: string;
}

export interface RateCalcInputs {
  start?: Date | string | null;
  end?: Date | string | null;
  baseRate: number;
  /** Active hourly billing-rate segments (one rate type, e.g. all StaffPay). */
  segments: BillingRate[];
  isSleepover?: boolean;
  travelKm?: number;
  sleepoverFlatRate?: FlatRate | null;
  travelFlatRate?: FlatRate | null;
}

function toDate(v: Date | string | null | undefined): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
}

function dayTypeOf(d: Date): string {
  const day = d.getDay();
  if (day === 6) return 'Saturday';
  if (day === 0) return 'Sunday';
  return 'Weekday';
}

const r2 = (n: number) => Math.round(n * 100) / 100;
const r4 = (n: number) => Math.round(n * 10000) / 10000;

/** Sleepover flat rate covers exactly this many hours of "inactive overnight". */
export const SLEEPOVER_HOURS = 8;

/**
 * Slice an hourly window by day-type + time-of-day segments. Returns the
 * priced slices plus the hours that were not covered by any active segment.
 * Internal helper — exported so backend tests can mirror it if needed.
 */
function sliceHourlySegments(
  start: Date,
  end: Date,
  baseRate: number,
  segments: BillingRate[],
): { slices: RateSlice[]; uncoveredHours: number; warning?: string } {
  if (!(end > start) || baseRate <= 0 || segments.length === 0) {
    return { slices: [], uncoveredHours: 0 };
  }
  const totalHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  const dayType = dayTypeOf(start);
  const daySegments = segments.filter(s => s.dayType === dayType);
  if (daySegments.length === 0) {
    return { slices: [], uncoveredHours: r2(totalHours), warning: `No active ${dayType} segment configured.` };
  }

  type Norm = { name: string; start: number; end: number; pct: number };
  const normalized: Norm[] = [];
  for (const seg of daySegments) {
    if (seg.timeEndMinutes <= seg.timeStartMinutes) {
      normalized.push({ name: seg.name, start: seg.timeStartMinutes, end: 1440, pct: seg.percentage });
      normalized.push({ name: seg.name, start: 0, end: seg.timeEndMinutes, pct: seg.percentage });
    } else {
      normalized.push({ name: seg.name, start: seg.timeStartMinutes, end: seg.timeEndMinutes, pct: seg.percentage });
    }
  }

  const shiftStartMin = start.getHours() * 60 + start.getMinutes();
  const shiftEndMinRaw = end.getHours() * 60 + end.getMinutes();
  const sameDay = start.toDateString() === end.toDateString();
  const overnight = !sameDay || shiftEndMinRaw <= shiftStartMin;
  const effectiveEnd = overnight ? shiftEndMinRaw + 1440 : shiftEndMinRaw;

  // For overnight shifts, every segment can overlap the shift TWICE — once
  // at its "today" position and once at "+1 day" (+1440 min). Considering
  // only one of those drops a chunk of the shift (e.g. a 22:00–02:00 active
  // window loses the 22:00–24:00 Evening match if we shift Evening to the
  // next day instead of also keeping its today position).
  type Pos = { name: string; start: number; end: number; pct: number };
  const positions: Pos[] = [];
  for (const seg of normalized) {
    positions.push({ name: seg.name, start: seg.start, end: seg.end, pct: seg.pct });
    if (overnight) {
      positions.push({ name: seg.name, start: seg.start + 1440, end: seg.end + 1440, pct: seg.pct });
    }
  }

  const slices: RateSlice[] = [];
  for (const seg of positions.sort((a, b) => a.start - b.start)) {
    const overlapStart = Math.max(shiftStartMin, seg.start);
    const overlapEnd = Math.min(effectiveEnd, seg.end);
    if (overlapEnd <= overlapStart) continue;
    const hours = r4((overlapEnd - overlapStart) / 60);
    const rate = r2(baseRate * seg.pct / 100);
    slices.push({
      name: seg.name,
      hours,
      rate,
      amount: r2(hours * rate),
      kind: 'Hourly',
    });
  }

  const coveredHours = slices.reduce((s, x) => s + x.hours, 0);
  const uncoveredHours = Math.max(0, r2(totalHours - coveredHours));
  return { slices, uncoveredHours };
}

export function computeShiftRateBreakdown(inputs: RateCalcInputs): RateBreakdown {
  const empty: RateBreakdown = { total: 0, segments: [], uncoveredHours: 0 };
  const start = toDate(inputs.start);
  const end = toDate(inputs.end);
  if (!start || !end) return empty;
  if (!(end > start)) return empty;

  const slices: RateSlice[] = [];
  let warning: string | undefined;
  let uncoveredHours = 0;

  if (inputs.isSleepover) {
    // ── Composite sleepover: any time beyond 8 hours runs at normal rates,
    //    the trailing 8 hours are covered by the flat sleepover rate.
    const totalHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    const activeHours = Math.max(0, totalHours - SLEEPOVER_HOURS);

    if (activeHours > 0) {
      const activeEnd = new Date(start.getTime() + activeHours * 60 * 60 * 1000);
      const sliced = sliceHourlySegments(start, activeEnd, inputs.baseRate, inputs.segments);
      slices.push(...sliced.slices);
      uncoveredHours = sliced.uncoveredHours;
      if (sliced.warning) warning = sliced.warning;
    }

    if (inputs.sleepoverFlatRate) {
      const amount = r2(inputs.sleepoverFlatRate.unitRate);
      slices.push({
        name: `Sleepover (${SLEEPOVER_HOURS}h overnight)`,
        hours: 1,
        rate: inputs.sleepoverFlatRate.unitRate,
        amount,
        kind: 'Sleepover',
      });
    } else {
      warning = (warning ? warning + ' ' : '')
        + 'No active Sleepover FlatRate configured (Billing Rates → Special Rates).';
    }
  } else {
    // ── Pure hourly shift ──
    const sliced = sliceHourlySegments(start, end, inputs.baseRate, inputs.segments);
    if (sliced.slices.length === 0 && sliced.uncoveredHours === 0 && !sliced.warning) {
      // Means baseRate <= 0 or no segments — propagate empty.
      return empty;
    }
    slices.push(...sliced.slices);
    uncoveredHours = sliced.uncoveredHours;
    if (sliced.warning) warning = sliced.warning;
  }

  // ── Travel: extra km × per-km rate, on top of either branch ──
  const km = inputs.travelKm ?? 0;
  if (km > 0) {
    if (inputs.travelFlatRate) {
      const k = r2(km);
      const amount = r2(k * inputs.travelFlatRate.unitRate);
      slices.push({
        name: `Travel (${k.toFixed(2)} km)`,
        hours: k,
        rate: inputs.travelFlatRate.unitRate,
        amount,
        kind: 'Travel',
      });
    } else {
      warning = (warning ? warning + ' ' : '') + 'Travel km recorded but no active Transport FlatRate configured.';
    }
  }

  const total = r2(slices.reduce((s, x) => s + x.amount, 0));
  return { total, segments: slices, uncoveredHours, warning };
}
